import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { cloneDeep, omit } from '@sama/micro-dash';
import { ShapeState3D } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { AbstractToolHelper } from '../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import { selectionToArray } from '../../../cross-team-shared/misc-utils';
import { ToolName } from '../../../cross-team-shared/tools-names';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { IN_PROGRESS_INDEX } from '../../../team-apis/core/reserved-shape-indexes';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { CuboidLocationState3D } from '../../state/cuboid-location-state-3d';
import { CuboidState3D } from '../../state/cuboid-state-3d';
import { PointCloudContentState } from '../../state/point-cloud-content-state';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';

export interface ShapeTool {
  name: ToolName;
  buildShape(initialValues: ShapeState3D): CuboidState3D;
  notifyDrawFinished(
    shape: ShapeState3D | undefined,
    creationMethod?: string,
  ): void;
}

export abstract class AbstractShapeToolHelper<
  T extends ShapeTool = ShapeTool,
> extends AbstractToolHelper {
  protected shellConnector: ShellConnector;
  protected store: PointCloudContentStore;
  protected shapeIndex: number;

  constructor(
    protected tool: T,
    protected inProgressCuboidStore: Store<CuboidState3D>,
    injector: Injector,
  ) {
    super();

    this.shellConnector = injector.get(shellConnectorToken);
    this.store = injector.get(PointCloudContentStore);
    this.shapeIndex = this.shellConnector.claimNextInternalId();
  }

  startListening() {
    this.store.batch(() => {
      const shape = this.tool.buildShape({
        type: 'cuboid',
        index: IN_PROGRESS_INDEX,
        tags: {},
        dynamic_tags: {},
        key_locations: [this.buildKeyLocation(this.store.state())],
      });
      this.inProgressCuboidStore.set(shape);
      this.shellConnector.modifyShapeSelection([IN_PROGRESS_INDEX]);
    });
  }

  override cancel(eventTrackingValue: EventTrackingValue) {
    super.cancel(eventTrackingValue);
    this.tool.notifyDrawFinished(undefined);

    // Shape tools set the selection to index 0, the in progress shape. When
    // cancelling the tool, we need to clear that in-progress selection.
    this.shellConnector.modifyShapeSelection(
      selectionToArray(
        omit(this.store.state().render.selection, IN_PROGRESS_INDEX),
      ),
    );
  }

  protected override complete() {
    const shape = cloneDeep(this.inProgressCuboidStore.state());
    shape.index = this.shapeIndex;
    this.tool.notifyDrawFinished(shape);
  }

  protected override cleanUp() {
    this.inProgressCuboidStore.delete();
    super.cleanUp();
  }

  protected buildKeyLocation(state: PointCloudContentState) {
    const keyLocation: CuboidLocationState3D = {
      frame_number: state.render.frameNumber,
      points: [],
      visibility: 1,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [0, 0, 0],
    };
    return keyLocation;
  }
}
